import { useState } from 'react';
import axios from 'axios';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { API_URL } from '../Config';

export default function ChangePassword() {
  const [form, setForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  });
  
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });
    setLoading(true);

    if (form.password !== form.password_confirmation) {
      setMessage({ text: 'Les mots de passe ne correspondent pas', type: 'error' });
      setLoading(false);
      return;
    }

    if (form.password.length < 8) {
      setMessage({ text: 'Le mot de passe doit contenir au moins 8 caractères', type: 'error' });
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/profile/password`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ 
        text: 'Mot de passe modifié avec succès', 
        type: 'success' 
      });
      
      setForm({ 
        current_password: '', 
        password: '', 
        password_confirmation: '' 
      });
      
      setShowPassword({ current: false, new: false, confirm: false });
      
    } catch (err) {
      const errorMsg = err.response?.data?.message || 
                      err.response?.data?.error || 
                      'Erreur lors du changement de mot de passe';
      
      setMessage({ 
        text: errorMsg, 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="min-h-screen  py-8">
      <div className="max-w-md mx-auto px-4">
        {/* En-tête */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Changer le mot de passe
          </h1>
          <p className="text-gray-600 text-sm">
            Mettez à jour votre mot de passe pour sécuriser votre compte
          </p>
        </div>

        {/* Carte principale */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          {/* Messages */}
          {message.text && (
            <div className={`p-4 ${message.type === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center">
                <div className={`mr-3 ${message.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                  {message.type === 'success' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-sm ${message.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                  {message.text}
                </span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6">
            {/* Mot de passe actuel */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe actuel
              </label>
              <div className="relative">
                <input
                  type={showPassword.current ? "text" : "password"}
                  value={form.current_password}
                  onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                  placeholder="Votre mot de passe actuel"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword.current ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Nouveau mot de passe */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword.new ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                  placeholder="Minimum 8 caractères"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword.new ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Utilisez au moins 8 caractères avec des lettres, chiffres et symboles
              </p>
            </div>

            {/* Confirmation */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  value={form.password_confirmation}
                  onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                  placeholder="Confirmez votre nouveau mot de passe"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword.confirm ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Bouton */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Modification...
                </div>
              ) : (
                'Modifier le mot de passe'
              )}
            </button>

            {/* Conseils */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Conseils pour un mot de passe sécurisé
              </h4>
              <ul className="text-xs text-gray-600 space-y-2">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1 mr-2"></div>
                  <span>Minimum 8 caractères</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1 mr-2"></div>
                  <span>Combinez lettres majuscules/minuscules</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1 mr-2"></div>
                  <span>Ajoutez des chiffres et symboles</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1 mr-2"></div>
                  <span>Évitez les mots courants</span>
                </li>
              </ul>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}